import { _getLogger } from '@doxyme/analytics';
import {
  type Specs,
  type CapabilityConfig,
  type Mutable,
  type Message,
  MessageClass,
} from '@doxyme/extensions-common';
import type Instance from './instance';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from './utils';

type ActionHandlerCb<T> = (payload: T, instance: Instance) => void;

type ActionHandler<T> = T extends [
  infer Action,
  infer Payload,
  infer _Transferable,
]
  ? [Action, ActionHandlerCb<Payload>]
  : T extends [infer Action, infer Payload]
    ? [Action, ActionHandlerCb<Payload>]
    : T extends [infer Action]
      ? [Action, ActionHandlerCb<never>]
      : never;

const logger = _getLogger('capability', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

export default class ExtensionCapability<
  Constraints extends Specs.Constraints = Specs.Constraints,
> {
  readonly id;

  protected readonly spec: Specs.CapabilitySpec;

  protected readonly instances: Record<string, Instance<Constraints>> = {};

  protected readonly handlers: ActionHandler<
    Constraints['actionPayloadTuple']
  >[] = [];

  /**
   * @param spec - The capability spec
   */
  constructor(spec: Specs.CapabilitySpec) {
    this.spec = spec;
    this.id = spec.id;
  }

  /**
   * @hidden
   */
  clear(): void {
    (this.instances as Mutable<typeof this.instances>) = {};
  }

  /**
   * @hidden
   */
  receive(instance: Instance, tuple: Constraints['actionPayloadTuple']): void {
    const [type, payload] = tuple;
    const handlers = this.handlers.filter(([ht]) => ht === type);

    for (const [, cb] of handlers) {
      (cb as ActionHandlerCb<unknown>)(payload, instance);
    }
  }

  /**
   * Check if the capability has a specific extension registered
   * @param instanceId - The extension instance ID to check
   * @returns Boolean indicating whether the extension is registered to this capability
   */
  hasInstance(instanceId: string): boolean {
    return instanceId in this.instances;
  }

  /**
   * @hidden
   */
  addInstance(instance: Instance): void {
    this.addInstanceBase(instance);
  }

  addInstanceBase(instance: Instance): void {
    this.instances[instance.id] = instance;
  }

  /**
   * @hidden
   */
  removeInstance(instance: Instance): void {
    delete this.instances[instance.id];
  }

  protected configsBy(instance: Instance): Constraints['config'][] {
    return instance.configsBy(this.id) as Constraints['config'][];
  }

  /**
   * Check if the config is valid based on the capability spec
   * @param config - The capability config to check
   * @returns Boolean indicating whether the config is valid
   */
  isValidConfig(config: CapabilityConfig): boolean {
    return this.spec.isValidConfig(config);
  }

  /**
   * @hidden
   */
  notifyAll(tuple: Constraints['eventPayloadTuple']): void {
    for (const instance of Object.values(this.instances)) {
      instance.notify(tuple);
    }
  }

  protected handleMessages(instance: Instance, messages: Message[]): void {
    for (const message of messages) {
      if (message.class === MessageClass.Action) {
        instance.receive([
          message.type,
          message.payload,
        ] as Constraints['actionPayloadTuple']);
      }
      if (message.class === MessageClass.Event) {
        instance.notify([
          message.type,
          message.payload,
        ] as Constraints['eventPayloadTuple']);
      }
    }
  }

  protected refineContext<Context extends Record<string, unknown>>(
    instance: Instance,
    context: Context,
  ): Context {
    const contextKeys = [
      ...new Set(this.configsBy(instance).flatMap((c) => c.context)),
    ];
    return Object.fromEntries(
      contextKeys.map((k) => [k, context[k as string]]),
    ) as Context;
  }

  protected getInstanceByManifestId(manifestId: string): Instance {
    const instance = Object.values(this.instances).find(
      (i) => i.manifest.id === manifestId,
    );

    if (!instance) {
      logErrorAndThrow(
        `[getInstanceByManifestId] Instance was not found for manifestId: ${manifestId}`,
        { manifest: { id: manifestId }, capability: this.id },
      );
    }

    return instance;
  }
}
